import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/translations'

/**
 * The language the PATIENT picked, kept for the life of the tab.
 *
 * It exists because the choice has to survive a reload, and this page reloads itself: the stall
 * watchdog spends up to two refreshes on a stage that has stopped moving (docs/decisions.md D-46),
 * and every one of them would otherwise hand the patient back the language their record was enrolled
 * in. Somebody who has already said they read Spanish should not have to say it again because a
 * download stalled.
 *
 * `sessionStorage` and keyed per link, exactly as `scan-session.ts` is and for the same reasons: the
 * tab is the session, and the choice belongs to this visit to this link rather than to the browser.
 *
 * Every access is guarded, because storage is not promised - a private window, a browser set to
 * block site data - and a page that cannot remember still works, it simply asks again.
 */
export function readLanguageChoice(token: string): SupportedLanguage | null {
  try {
    const stored = sessionStorage.getItem(keyFor(token))
    return SUPPORTED_LANGUAGES.find((supported) => supported === stored) ?? null
  } catch {
    return null
  }
}

export function storeLanguageChoice(token: string, language: SupportedLanguage): void {
  try {
    sessionStorage.setItem(keyFor(token), language)
  } catch {
    // The choice holds for this page either way; it is only the reload that forgets it.
  }
}

function keyFor(token: string): string {
  return `scan-language:${token}`
}
